import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { 
  Home, 
  StickyNote, 
  Plus, 
  Search, 
  User, 
  Settings,
  BarChart3,
  X,
  Sun,
  Moon,
  Monitor,
  LogOut,
  Cloud,
  Trash2
} from 'lucide-react'
import ImportButton from '../common/ImportButton'
import { clsx } from 'clsx'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalMode } from '../../contexts/LocalModeContext'
import { useSensitiveSession } from '../../contexts/SensitiveSessionContext'
import { useCloudStorage } from '../../hooks/useCloudStorage'
import SettingsModal from '../common/SettingsModal'
import ConfirmationModal from '../common/ConfirmationModal'
import CloudStorageUsage from '../cloud/CloudStorageUsage'

/**
 * Sidebar Component
 * 
 * Responsive navigation sidebar with main navigation links.
 * Collapses on mobile and can be toggled open/closed.
 */
const Sidebar = ({ isOpen, onClose, onNotesImported }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isDarkMode, currentTheme, toggleTheme } = useTheme()
  const { user, logout } = useAuth()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false)
  const { isLocalMode, clearAllLocalData } = useLocalMode()
  const { revoke: revokeSensitiveSession } = useSensitiveSession()
  const { isConnected: isCloudConnected } = useCloudStorage()
  const [isDeleteAllConfirmOpen, setIsDeleteAllConfirmOpen] = useState(false)

  // Debug: Log the onNotesImported prop (commented out to reduce noise)
  // console.log('Sidebar: onNotesImported prop:', !!onNotesImported)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true)
    if (onClose) {
      onClose()
    }
  }

  const handleOpenSettings = () => {
    setIsSettingsOpen(true)
    if (onClose) {
      onClose()
    }
  }

  const handleDeleteAllClick = () => {
    setIsDeleteAllConfirmOpen(true)
    if (onClose) {
      onClose()
    }
  }

  const handleDeleteAllConfirm = async () => {
    try {
      console.log('🗑️ Sidebar: Starting delete all local notes process')
      
      // Revoke sensitive session first
      revokeSensitiveSession(false, 'delete_all')
      
      await clearAllLocalData()
      console.log('✅ Sidebar: Local data cleared, closing modal and navigating to login')
      
      setIsDeleteAllConfirmOpen(false)
      
      // Force navigation to login page after a short delay to ensure state is updated
      setTimeout(() => {
        console.log('🔄 Sidebar: Navigating to login page')
        navigate('/login')
      }, 100)
      
    } catch (error) {
      console.error('❌ Sidebar: Failed to delete all local notes:', error)
      setIsDeleteAllConfirmOpen(false)
    }
  }

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      description: 'Overview and statistics'
    },
    {
      name: 'Notes',
      href: '/notes',
      icon: StickyNote,
      description: 'View and manage notes'
    }
  ]

  const accountNavigation = [
    {
      name: 'Settings',
      action: handleOpenSettings,
      icon: Settings,
      description: 'Account and preferences'
    }
  ]

  const isActivePage = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path
    }
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleLinkClick = () => {
    // Close mobile menu when link is clicked
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 bg-gray-100 dark:bg-gray-800 amoled:bg-gray-100 shadow-lg transform transition-transform duration-300 ease-in-out md:translate-x-0 md:relative md:h-full',
        isOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full pb-[60px]"> 
          {/* Mobile close button */}
          <div className="flex items-center justify-end p-4 md:hidden">
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 pb-4 space-y-1">
            {/* Main Navigation */}
            <div className="space-y-1">
              <div className="flex items-center justify-between px-3 py-2">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wider">
                  NoteSpark.ai
                </h3>
                {isLocalMode && (
                  <span className="relative inline-flex items-center px-3 py-1 text-xs font-medium bg-red-500 text-white dark:bg-red-600 dark:text-white before:content-[''] before:absolute before:top-0 before:right-[-8px] before:w-0 before:h-0 before:border-l-[8px] before:border-l-red-500 dark:before:border-l-red-600 before:border-t-[12px] before:border-t-transparent before:border-b-[12px] before:border-b-transparent">
                    Local mode
                  </span>
                )}
              </div>
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = isActivePage(item.href)

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleLinkClick}
                    className={clsx(
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-300'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                    )}
                  >
                    <Icon 
                      className={clsx(
                        'mr-3 h-5 w-5 flex-shrink-0',
                        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                      )} 
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span>{item.name}</span>
                        {isActive && (
                          <div className="w-2 h-2 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden lg:block">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

            {/* Import Section */}
            {onNotesImported && (
              <div className="space-y-1 mb-4">
                <h3 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Import
                </h3>
                <div className="px-3 py-2">
                  <ImportButton onNotesImported={onNotesImported} />
                </div>
              </div>
            )}

            {/* Account Navigation */}
            <div className="space-y-1">
              <h3 className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Account
              </h3>
              {accountNavigation.map((item) => {
                const Icon = item.icon

                if (item.action) {
                  return (
                    <button
                      key={item.name}
                      onClick={item.action}
                      className={clsx(
                        'group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors text-left',
                        'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                      )}
                    >
                      <Icon 
                        className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400" 
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span>{item.name}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden lg:block">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  )
                }

                const isActive = isActivePage(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleLinkClick}
                    className={clsx(
                      'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-300'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-200'
                    )}
                  >
                    <Icon 
                      className={clsx(
                        'mr-3 h-5 w-5 flex-shrink-0',
                        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                      )} 
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span>{item.name}</span>
                        {isActive && (
                          <div className="w-2 h-2 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 hidden lg:block">
                        {item.description}
                      </p>
                    </div>
                  </Link>
                )
              })}
            </div>
          </nav>

          {/* Theme Toggle */}
          <div className="flex-shrink-0 p-4">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-200 amoled:bg-black amoled:hover:bg-gray-900 transition-colors duration-200"
              title={
                currentTheme === 'light' ? 'Switch to dark mode' :
                currentTheme === 'dark' ? 'Switch to Black mode' :
                'Switch to light mode'
              }
            >
              <span className="flex-shrink-0">
                {currentTheme === 'light' ? (
                  <Sun className="h-5 w-5 text-gray-400" />
                ) : currentTheme === 'dark' ? (
                  <Moon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                ) : (
                  <Monitor className="h-5 w-5 text-gray-400 amoled:text-gray-500" />
                )}
              </span>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <span>
                    {currentTheme === 'light' ? 'Light Mode' :
                     currentTheme === 'dark' ? 'Dark Mode' :
                     'Black Mode'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 amoled:text-gray-400 mt-0.5 hidden lg:block text-left">
                  Click to cycle themes
                </p>
              </div>
            </button>
          </div>

          {/* Sign Out/Sign Up Button */}
          <div className="flex-shrink-0 p-4">
            {isLocalMode ? (
              <>
                <Link
                  to="/signup"
                  className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300 transition-colors duration-200"
                  title="Sign up to sync your notes to the cloud"
                >
                  <span className="flex-shrink-0">
                    <Cloud className="h-5 w-5" />
                  </span>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span>Sign Up to Sync</span>
                    </div>
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-0.5 hidden lg:block">
                      Backup your local notes
                    </p>
                  </div>
                </Link>
                <button
                  onClick={handleDeleteAllClick}
                  className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 transition-colors duration-200 mt-2"
                  title="Delete all local notes"
                >
                  <span className="flex-shrink-0">
                    <Trash2 className="h-5 w-5" />
                  </span>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span>Sign out and Delete Notes</span>
                    </div>
                    <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 hidden lg:block text-left">
                      from your device
                    </p>
                  </div>
                </button>
              </>
            ) : (
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 transition-colors duration-200"
                title="Sign out of your account"
              >
                <span className="flex-shrink-0">
                  <LogOut className="h-5 w-5" />
                </span>
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <span>Sign Out</span>
                  </div>
                  <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 hidden lg:block">
                    {user?.email}
                  </p>
                </div>
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
            {!isLocalMode && isCloudConnected ? (
              <CloudStorageUsage />
            ) : (
              <div className="flex items-center">
                <BarChart3 className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Note Statistics</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Track your progress</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
      />

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out? You'll need to log in again to access your notes."
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Delete All Local Notes Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteAllConfirmOpen}
        onClose={() => setIsDeleteAllConfirmOpen(false)}
        onConfirm={handleDeleteAllConfirm}
        title="Clear All Local Notes"
        message="Are you sure you want to clear all local notes? This action cannot be undone."
        confirmText="Clear All"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  )
}

export default Sidebar 